import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fonts, radii } from '@/theme';
import { Mono, useModeTokens } from '@/common/components/ui';
import { ANCHOR, HOW_STEPS } from './content';
import { Eyebrow, Headline, useSectionTop } from './primitives';

const HISTORY = [
  {
    version: 14,
    title: 'Vocal comp + new bridge',
    time: 'Today, 3:42 PM',
    changes: '8 changes',
    created: '3 created',
    updated: '5 updated',
    latest: true,
  },
  {
    version: 13,
    title: 'Drum bus rebuild',
    time: 'Today, 12:18 PM',
    changes: '4 changes',
    created: '1 created',
    updated: '3 updated',
  },
  {
    version: 12,
    title: 'Arrangement lock',
    time: 'Yesterday, 6:05 PM',
    changes: '7 changes',
    created: '2 created',
    updated: '4 updated',
    deleted: '1 deleted',
    expanded: true,
  },
];

/** Product-faithful version history with the real download and sync actions. */
export const LandingHowItWorks: React.FC = () => {
  const t = useModeTokens();
  const sectionTop = useSectionTop('56px');

  return (
    <Box
      id="lp-versions"
      sx={{ ...ANCHOR, padding: { xs: '0 20px 48px', md: '0 40px 60px' } }}
    >
      <Box
        sx={{
          ...sectionTop,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
          },
          gap: { xs: '32px', md: '44px' },
          alignItems: 'center',
        }}
      >
        <Box>
          <Eyebrow mb="16px">01 / MAKE ROOM FOR ANOTHER IDEA</Eyebrow>
          <Headline sx={{ marginBottom: '14px', maxWidth: '18ch' }}>
            Go back to the
            <br />
            <span className="lp-gradient-text">version that worked.</span>
          </Headline>
          <Box
            component="p"
            sx={{
              margin: '0 0 26px',
              fontSize: '14.5px',
              lineHeight: 1.6,
              color: t.text.secondary,
              maxWidth: '46ch',
            }}
          >
            Every checkpoint records its file-level changes. Open an older
            version to download a copy, or sync it back to your linked project
            folder after reviewing the overwrite warning.
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {HOW_STEPS.map(([title, body], i) => (
              <Box key={title} sx={{ display: 'flex', gap: '14px' }}>
                <Box
                  component="span"
                  sx={{
                    width: 26,
                    height: 26,
                    flex: 'none',
                    borderRadius: `${radii.sm}px`,
                    background: alpha(t.colors.accentFill, 0.14),
                    color: t.colors.accentText,
                    fontFamily: fonts.mono,
                    fontWeight: 600,
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {i + 1}
                </Box>
                <Box>
                  <Box sx={{ fontWeight: 600, fontSize: '14px' }}>{title}</Box>
                  <Box
                    sx={{
                      fontSize: '13px',
                      color: t.text.secondary,
                      marginTop: '4px',
                      lineHeight: 1.55,
                    }}
                  >
                    {body}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          className="lp-proof lp-gloss-panel"
          sx={{
            border: `1px solid ${t.borders.medium}`,
            borderRadius: `${radii['2xl']}px`,
            background: t.surfaces.cardGradientSoft,
            padding: { xs: '16px', md: '20px' },
            boxShadow: t.shadows.modal,
          }}
        >
          <Box sx={{ marginBottom: '16px' }}>
            <Mono size={9} sx={{ display: 'block', mb: 1.5 }}>
              NIGHTDRIVE · ILLUSTRATIVE VERSION HISTORY
            </Mono>
            <Box sx={{ fontSize: '16px', fontWeight: 700 }}>
              Version History (14 versions)
            </Box>
            <Box
              sx={{
                color: t.text.secondary,
                fontSize: '11.5px',
                marginTop: '4px',
              }}
            >
              Newest first. Expand a version to inspect its file-level changes.
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              paddingLeft: '18px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 5,
                top: 18,
                bottom: 18,
                width: 2,
                borderRadius: 99,
                background: t.borders.medium,
              },
            }}
          >
            {HISTORY.map((version) => (
              <Box
                key={version.version}
                className="lp-gloss-inset"
                sx={{
                  position: 'relative',
                  border: `1px solid ${
                    version.latest || version.expanded
                      ? alpha(t.colors.accentFill, 0.46)
                      : t.borders.subtle
                  }`,
                  borderRadius: `${radii.lg}px`,
                  background: version.expanded
                    ? alpha(t.colors.accentFill, 0.055)
                    : t.surfaces.fill,
                  padding: { xs: '12px', sm: '13px 14px' },
                  boxShadow: version.expanded ? t.shadows.cardFlat : 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -18,
                    top: 22,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    border: `2px solid ${
                      version.latest || version.expanded
                        ? t.colors.accentFill
                        : t.borders.stronger
                    }`,
                    background: t.surfaces.screen,
                    boxShadow: version.latest
                      ? `0 0 0 4px ${alpha(t.colors.accentFill, 0.14)}`
                      : 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: '10px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 18,
                        marginTop: '4px',
                        color: version.expanded
                          ? t.colors.accentText
                          : t.text.muted,
                        transform: version.expanded
                          ? 'rotate(180deg)'
                          : 'rotate(0)',
                      }}
                    />
                    <Box
                      sx={{
                        borderRadius: '6px',
                        background: version.latest
                          ? t.colors.accentFill
                          : t.surfaces.trackSoft,
                        color: version.latest
                          ? t.colors.onAccent
                          : t.text.primary,
                        padding: '7px 9px',
                        fontFamily: fonts.mono,
                        fontSize: '11px',
                        fontWeight: 700,
                      }}
                    >
                      v{version.version}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontWeight: 650, fontSize: '13px' }}>
                        {version.title}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '5px',
                          alignItems: 'center',
                          marginTop: '5px',
                        }}
                      >
                        <Mono size={8.5}>{version.time}</Mono>
                        {version.latest && (
                          <Mono
                            size={8}
                            weight={700}
                            color={t.colors.accentText}
                            sx={{
                              borderRadius: '999px',
                              background: alpha(t.colors.accentFill, 0.12),
                              padding: '3px 6px',
                            }}
                          >
                            LATEST
                          </Mono>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: '6px',
                      paddingLeft: { xs: '26px', sm: 0 },
                    }}
                  >
                    {[
                      [DownloadOutlinedIcon, 'Download'],
                      [SyncOutlinedIcon, 'Sync'],
                    ].map(([Icon, label]) => (
                      <Box
                        key={label as string}
                        component="span"
                        sx={{
                          border: `1px solid ${t.borders.default}`,
                          borderRadius: '6px',
                          padding: '6px 8px',
                          display: 'inline-flex',
                          gap: '5px',
                          alignItems: 'center',
                          color:
                            label === 'Sync' && version.expanded
                              ? t.colors.accentText
                              : t.text.secondary,
                          fontSize: '10px',
                          fontWeight: 650,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Icon sx={{ fontSize: 14 }} />
                        {label as string}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '5px',
                    marginTop: '9px',
                    paddingLeft: '26px',
                  }}
                >
                  {[
                    [version.changes, t.text.secondary],
                    [version.created, t.colors.green],
                    [version.updated, t.colors.cyan],
                    ...(version.deleted
                      ? ([[version.deleted, t.colors.accentText]] as const)
                      : []),
                  ].map(([label, color]) => (
                    <Mono
                      key={label}
                      size={8}
                      weight={600}
                      color={color}
                      sx={{
                        border: `1px solid ${alpha(color, 0.3)}`,
                        borderRadius: '5px',
                        padding: '4px 6px',
                      }}
                    >
                      {label.toUpperCase()}
                    </Mono>
                  ))}
                </Box>

                {version.expanded && (
                  <Box sx={{ ml: '26px', mt: 1.5, display: 'grid', gap: 1 }}>
                    {[
                      {
                        title: '2 CREATED',
                        color: t.colors.green,
                        files: [
                          '+ Samples/bridge.wav',
                          '+ Samples/texture.wav',
                        ],
                      },
                      {
                        title: '4 UPDATED',
                        color: t.colors.cyan,
                        files: [
                          '~ Nightdrive.als',
                          '~ Renders/rough-master.wav',
                          '~ Samples/drums.wav',
                          '~ Samples/bass.wav',
                        ],
                      },
                      {
                        title: '1 DELETED',
                        color: t.text.secondary,
                        files: ['− Samples/old-take.wav'],
                      },
                    ].map((group) => (
                      <Box
                        key={group.title}
                        sx={{
                          borderLeft: '2px solid',
                          borderColor: group.color,
                          background: t.surfaces.trackSoft,
                          p: '8px 10px',
                          borderRadius: '0 6px 6px 0',
                        }}
                      >
                        <Mono size={8} weight={700} color={group.color}>
                          {group.title}
                        </Mono>
                        {group.files.map((file) => (
                          <Box
                            key={file}
                            sx={{
                              mt: 0.5,
                              fontSize: 10,
                              color: t.text.secondary,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {file}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
                {version.expanded && (
                  <Box
                    sx={{
                      margin: '10px 0 0 26px',
                      borderLeft: `2px solid ${t.colors.accentFill}`,
                      borderRadius: '0 6px 6px 0',
                      background: alpha(t.colors.accentFill, 0.075),
                      padding: '8px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: '6px 12px',
                    }}
                  >
                    <Box>
                      <Box sx={{ fontSize: '11px', fontWeight: 700 }}>
                        Go back to v{version.version}
                      </Box>
                      <Box sx={{ fontSize: '9.5px', color: t.text.secondary }}>
                        Sync shows a confirmation before replacing local files.
                      </Box>
                    </Box>
                    <Mono
                      size={8.5}
                      weight={700}
                      color={t.colors.accentText}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      SYNC TO THIS VERSION
                    </Mono>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingHowItWorks;
